import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import { getTagUrl } from '../../utils/UrlUtilities';
import {
    BCS_LEVEL,
    TAGLEVELS,
    BOARD_VALUES,
    DIVISION_VALUES,
    UNIT_VALUES,
    isBoardLevel,
    isDivisionLevel,
    isUnitLevel,
    isLanguageLevel,
    sortBcsTerms,
} from '../../utils/Constants';
import CloseButton from '../ui/CloseButton';
import YearPicker from '../ui/YearPicker';

// Shared "Bulk Edit" modal used by both the Parsed Drafts grid and the Question
// Management grid. Every field defaults to "— leave unchanged —"; only the fields
// the admin actually picks are returned to onApply, which the parent sends to the
// bulk-update endpoint. Dropdown wiring mirrors ParsedDraftEditForm / QuestionAddForm.
const EMPTY_FIELDS = {
    level: '', subject: '', topic: '', exam: '',
    board: '', division: '', unit: '', bcs_term: '', year: '',
};

const BulkEditModal = ({ open, count = 0, onClose, onApply }) => {
    const CallApi = useApiCaller();
    // useApiCaller returns a fresh function each render — pin it via a ref so the
    // tag-fetch effect doesn't re-fire every render (same pattern as ParsedDraftEditForm).
    const callApiRef = useRef(CallApi);
    useEffect(() => { callApiRef.current = CallApi; });

    const [fields, setFields] = useState(EMPTY_FIELDS);
    const [availableTags, setAvailableTags] = useState([]);
    const [busy, setBusy] = useState(false);
    const [localError, setLocalError] = useState('');

    // Reset the form each time the modal opens.
    useEffect(() => {
        if (open) {
            setFields(EMPTY_FIELDS);
            setAvailableTags([]);
            setLocalError('');
            setBusy(false);
        }
    }, [open]);

    const level = fields.level;

    const fetchTagsForLevel = useCallback(async () => {
        if (!level) {
            setAvailableTags([]);
            return;
        }
        const res = await callApiRef.current({ url: getTagUrl({ level }), method: 'GET', token: true });
        setAvailableTags(res.success ? (res.data.tags || []) : []);
    }, [level]);

    useEffect(() => { fetchTagsForLevel(); }, [fetchTagsForLevel]);

    const set = (patch) => setFields(prev => ({ ...prev, ...patch }));

    const tagsOfType = (type) => [
        ...new Set(availableTags.filter(t => t.tag_type === type && t.level === level).map(t => t.name))
    ];

    // Topics for the chosen level, narrowed to the chosen subject (topics with no
    // subject linkage are kept, degrading to level-only — same as the single editor).
    const topicOptions = () => {
        const sub = (fields.subject || '').trim();
        return [...new Set(
            availableTags
                .filter(t => t.tag_type === 'TOPIC' && t.level === level)
                .filter(t => {
                    const s = t.subject || (t.metadata && t.metadata.subject) || '';
                    return !s || !sub || s === sub;
                })
                .map(t => t.name)
        )];
    };

    const bcsTermNames = level === BCS_LEVEL
        ? sortBcsTerms(availableTags.filter(t => t.tag_type === 'BCS_TERM' && t.level === BCS_LEVEL).map(t => t.name))
        : [];

    // Picking a level clears the dependent fields (their old values no longer apply).
    const handleLevelChange = (newLevel) => {
        set({ level: newLevel, subject: '', topic: '', exam: '', board: '', division: '', unit: '', bcs_term: '' });
    };

    const hasChanges = Object.values(fields).some(v => (v || '').toString().trim());

    const handleApply = async () => {
        setLocalError('');
        if (!hasChanges) {
            setLocalError('Nothing to change — every field is left unchanged.');
            return;
        }
        setBusy(true);
        try {
            const ok = await onApply(fields);
            if (ok) onClose();
        } catch (e) {
            setLocalError(e?.message || 'Bulk edit failed.');
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title mb-0">Bulk Edit — {count} selected</h5>
                        <CloseButton onClick={onClose} />
                    </div>
                    <div className="modal-body">
                        <p className="small text-muted">
                            Only fields you change here are applied; anything left on
                            "— leave unchanged —" keeps each item's current value.
                        </p>
                        {localError && <div className="alert alert-danger py-2 mb-3">{localError}</div>}

                        <div className="mb-3">
                            <label className="form-label">Level</label>
                            <select className="form-select" value={fields.level} onChange={e => handleLevelChange(e.target.value)}>
                                <option value="">— leave unchanged —</option>
                                {TAGLEVELS.map(item => item.submenu
                                    ? <optgroup key={item.value} label={item.label}>{item.submenu.map(sub => <option key={sub.value} value={sub.value}>{sub.label}</option>)}</optgroup>
                                    : <option key={item.value} value={item.value}>{item.label}</option>
                                )}
                            </select>
                        </div>

                        {isDivisionLevel(level) && (
                            <div className="mb-3">
                                <label className="form-label">Division</label>
                                <select className="form-select" value={fields.division} onChange={e => set({ division: e.target.value })}>
                                    <option value="">— leave unchanged —</option>
                                    {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                        )}

                        {isUnitLevel(level) && (
                            <div className="mb-3">
                                <label className="form-label">Unit</label>
                                <select className="form-select" value={fields.unit} onChange={e => set({ unit: e.target.value })}>
                                    <option value="">— leave unchanged —</option>
                                    {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label">Subject</label>
                            <select className="form-select" value={fields.subject} onChange={e => set({ subject: e.target.value, topic: '' })} disabled={!level}>
                                <option value="">— leave unchanged —</option>
                                {tagsOfType('SUBJECT').map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            {!level && <small className="form-text text-muted">Pick a level first to load subjects.</small>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Topic</label>
                            <select className="form-select" value={fields.topic} onChange={e => set({ topic: e.target.value })} disabled={!level || topicOptions().length === 0}>
                                <option value="">— leave unchanged —</option>
                                {topicOptions().map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>

                        {!isLanguageLevel(level) && (
                            <div className="mb-3">
                                <label className="form-label">Exam</label>
                                <select className="form-select" value={fields.exam} onChange={e => set({ exam: e.target.value })} disabled={!level || tagsOfType('EXAM').length === 0}>
                                    <option value="">— leave unchanged —</option>
                                    {tagsOfType('EXAM').map(x => <option key={x} value={x}>{x}</option>)}
                                </select>
                            </div>
                        )}

                        {isBoardLevel(level) && (
                            <div className="mb-3">
                                <label className="form-label">Board</label>
                                <select className="form-select" value={fields.board} onChange={e => set({ board: e.target.value })}>
                                    <option value="">— leave unchanged —</option>
                                    {BOARD_VALUES.map(b => <option key={b} value={b}>{b}</option>)}
                                </select>
                            </div>
                        )}

                        {level === BCS_LEVEL && (
                            <div className="mb-3">
                                <label className="form-label">BCS Term</label>
                                <select className="form-select" value={fields.bcs_term} onChange={e => set({ bcs_term: e.target.value })} disabled={bcsTermNames.length === 0}>
                                    <option value="">— leave unchanged —</option>
                                    {bcsTermNames.map(name => <option key={name} value={name}>{name}</option>)}
                                </select>
                            </div>
                        )}

                        {/* Exam year — dropped server-side for BCS, so hide it there. */}
                        {level !== BCS_LEVEL && (
                            <div className="mb-3">
                                <label className="form-label">Exam Year</label>
                                <YearPicker value={fields.year} onChange={y => set({ year: y })} />
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={handleApply} disabled={busy || !hasChanges}>
                            {busy ? 'Applying…' : `Apply to ${count} selected`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkEditModal;
